import type { FoundryDecisionLedgerRecord, FoundryV2LecV2Record } from "../api/foundryV2Client";

function statusTone(status: "ready" | "watch" | "blocked"): string {
  switch (status) {
    case "ready":
      return "border-emerald-200 bg-emerald-50 text-emerald-700";
    case "watch":
      return "border-amber-200 bg-amber-50 text-amber-700";
    default:
      return "border-red-200 bg-red-50 text-red-700";
  }
}

type Props = {
  decisionLedger: FoundryDecisionLedgerRecord | null;
  lec: FoundryV2LecV2Record | null;
  isLoading: boolean;
  error: string | null;
};

export function FoundryDecisionLedgerPanel({
  decisionLedger,
  lec,
  isLoading,
  error,
}: Props): JSX.Element {
  const readiness = decisionLedger?.decisionLedger.readiness;
  const lockedCriticalEntries =
    decisionLedger?.decisionLedger.entries.filter((entry) => entry.criticality === "critical" && entry.locked) ?? [];
  const pendingCriticalEntries =
    decisionLedger?.decisionLedger.entries.filter((entry) => entry.criticality === "critical" && !entry.locked) ?? [];
  const topEndpoints = lec?.lec.serviceSurface.endpoints.slice(0, 4) ?? [];
  const topQualityGates = lec?.lec.deliveryModel.qualityGates.slice(0, 4) ?? [];

  return (
    <section
      className="rounded-2xl border border-slate-200 bg-white/95 p-5 shadow-[0_20px_60px_rgba(15,23,42,0.08)]"
      data-testid="foundry-decision-ledger-panel"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-3xl">
          <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Decision ledger + LEC v2</div>
          <h3 className="mt-2 text-xl font-semibold text-slate-950">What Jarvis is allowed to execute, in a normalized contract</h3>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            The ledger freezes design choices section by section. LEC v2 compiles the locked design, service surface, delivery controls,
            and assembly intent into the execution contract Jarvis must follow.
          </p>
        </div>
      </div>

      {error ? (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      ) : null}

      {isLoading && !decisionLedger && !lec ? (
        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
          Loading decision ledger and LEC v2...
        </div>
      ) : null}

      {decisionLedger && lec ? (
        <>
          <div className="mt-5 grid gap-3 xl:grid-cols-[1.2fr_0.9fr_0.9fr_0.9fr]">
            <div className="rounded-2xl border border-slate-200 bg-[linear-gradient(135deg,#f8fafc_0%,#eef4ff_100%)] p-4">
              <div className="flex items-center gap-2">
                <span className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${statusTone(readiness?.status ?? "blocked")}`}>
                  {readiness?.status ?? "blocked"}
                </span>
                <span className="text-xs text-slate-500">
                  {readiness?.criticalLockedCount ?? 0}/{readiness?.criticalTotalCount ?? 0} critical choices locked
                </span>
              </div>
              <div className="mt-3 text-sm text-slate-700">
                {readiness?.canCompileLecV2
                  ? "Foundry has locked all critical decisions required to compile the execution contract for Jarvis."
                  : "Jarvis should stay constrained until the remaining critical decisions are explicitly promoted."}
              </div>
              <ul className="mt-3 space-y-2 text-xs text-slate-600">
                {(readiness?.nextMoves ?? []).map((move) => (
                  <li key={move} className="rounded-lg border border-white/70 bg-white/80 px-3 py-2">
                    {move}
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Jarvis launch posture</div>
              <div className="mt-2 text-sm font-semibold text-slate-900">
                {lec.lec.executionControl.canLaunchJarvis ? "Launchable" : "Blocked"}
              </div>
              <div className="mt-1 text-xs text-slate-600">
                {lec.lec.app.name} · {lec.lec.app.domainFamily} · {lec.lec.app.operatingModel}
              </div>
              <div className="mt-3 space-y-1 text-xs text-slate-600">
                <div>Archetype: {lec.lec.app.archetypeId}</div>
                <div>Delivery: {lec.lec.assemblyIntent.deliveryMode}</div>
                <div>Stack: {lec.lec.architecture.preferredStack}</div>
                <div>Topology: {lec.lec.architecture.deploymentTopology}</div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Service surface</div>
              <div className="mt-2 text-sm font-semibold text-slate-900">
                {lec.lec.serviceSurface.endpoints.length} endpoints · {lec.lec.assemblyIntent.serviceIds.length} services
              </div>
              <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
                {lec.lec.serviceSurface.integrationTargets.map((target) => (
                  <span key={target} className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-slate-700">
                    {target}
                  </span>
                ))}
              </div>
              <div className="mt-3 text-xs text-slate-600">
                {lec.lec.serviceSurface.mandatoryControls.length} mandatory controls · {lec.lec.serviceSurface.envKeys.length} env keys
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Artifacts</div>
              <div className="mt-3 space-y-2 text-xs">
                <a className="block font-medium text-blue-700 underline" href={decisionLedger.artifactUrls.decisionLedger}>
                  decision_ledger.json
                </a>
                <a className="block font-medium text-blue-700 underline" href={lec.artifactUrls.lecV2}>
                  lec_v2.json
                </a>
              </div>
            </div>
          </div>

          <div className="mt-5 grid gap-4 xl:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Critical decisions</div>
                <div className="text-xs text-slate-500">
                  {lockedCriticalEntries.length} locked · {pendingCriticalEntries.length} pending
                </div>
              </div>
              <div className="mt-3 space-y-3">
                {lockedCriticalEntries.slice(0, 4).map((entry) => (
                  <div key={entry.decisionId} className="rounded-xl border border-white bg-white px-4 py-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-sm font-semibold text-slate-900">{entry.sectionTitle}</div>
                      <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
                        locked
                      </span>
                    </div>
                    <div className="mt-1 text-sm text-slate-700">{entry.selectedLabel ?? entry.recommendedLabel ?? entry.decision}</div>
                    <div className="mt-2 text-xs text-slate-600">{entry.decision}</div>
                  </div>
                ))}
                {!lockedCriticalEntries.length ? (
                  <div className="rounded-xl border border-white bg-white px-4 py-3 text-sm text-slate-600">
                    No critical decision has been locked yet.
                  </div>
                ) : null}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Execution contract preview</div>
              <div className="mt-3 space-y-3">
                <div className="rounded-xl border border-white bg-white px-4 py-3">
                  <div className="text-sm font-semibold text-slate-900">Quality gates</div>
                  <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
                    {topQualityGates.map((gate) => (
                      <span key={gate} className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-slate-700">
                        {gate}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="rounded-xl border border-white bg-white px-4 py-3">
                  <div className="text-sm font-semibold text-slate-900">Primary endpoints</div>
                  <div className="mt-2 space-y-2 text-xs text-slate-600">
                    {topEndpoints.map((endpoint) => (
                      <div key={`${endpoint.method}-${endpoint.path}`} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                        <span className="font-semibold text-slate-800">{endpoint.method}</span> {endpoint.path} · {endpoint.description}
                      </div>
                    ))}
                    {!topEndpoints.length ? (
                      <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                        No endpoint has been formalized yet in the v1 technical spec.
                      </div>
                    ) : null}
                  </div>
                </div>
                <div className="rounded-xl border border-white bg-white px-4 py-3">
                  <div className="text-sm font-semibold text-slate-900">Jarvis controls</div>
                  <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
                    {lec.lec.serviceSurface.mandatoryControls.map((control) => (
                      <span key={control} className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-slate-700">
                        {control}
                      </span>
                    ))}
                  </div>
                  <ul className="mt-2 space-y-2 text-xs text-slate-600">
                    {lec.lec.executionControl.nextMoves.map((move) => (
                      <li key={move} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                        {move}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : null}
    </section>
  );
}
